/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  forwardRef,
  OnInit,
  Injector,
  input,
  signal,
  computed,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewContainerRef,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, FormsModule } from '@angular/forms';
import { CustomErrorDisplayComponent } from '../custom-error-display/custom-error-display.component';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Overlay, OverlayRef, OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

interface CalendarDay {
  date: Date;
  day: number;
  id: number;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  isRangeStart: boolean;
  isRangeEnd: boolean;
  isInPreviewRange: boolean;
}

@Component({
  selector: 'lib-custom-date-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    CustomErrorDisplayComponent,
    OverlayModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDateInputComponent),
      multi: true,
    },
    DatePipe,
  ],
  templateUrl: './custom-date-input.component.html',
  styles: [
    `
      .custom-tooltip-container {
        position: relative;
        display: inline-block;
      }
      .custom-tooltip-text {
        visibility: hidden;
        width: 160px;
        background-color: #e8f1f8; /* primary_light */
        color: #1c2229; /* basic */
        text-align: center;
        border-radius: 8px;
        padding: 8px;
        position: absolute;
        z-index: 50;
        bottom: 125%;
        left: 50%;
        margin-left: -80px;
        opacity: 0;
        transition: opacity 0.3s;
        box-shadow:
          0 4px 6px -1px rgb(0 0 0 / 0.1),
          0 2px 4px -2px rgb(0 0 0 / 0.1);
        font-size: 11px;
        font-weight: normal;
        pointer-events: none;
      }
      .custom-tooltip-text::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: #e8f1f8 transparent transparent transparent;
      }
      .custom-tooltip-container:hover .custom-tooltip-text {
        visibility: visible;
        opacity: 1;
      }
    `,
  ],
})
export class CustomDateInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  label = input<string>('');
  placeholder = input<string>('');
  name = input<string>('');
  selectionMode = input<'single' | 'range'>('single');
  hint = input<string | null>(null);
  tooltip = input<string>('');
  errors = input<{ [key: string]: string }>({});
  displayError = input<boolean>(true);
  disabled = input<boolean>(false);

  readonly effectiveName = computed(() => {
    const currentName = this.name();
    if (currentName) return currentName;
    const currentLabel = this.label();
    if (currentLabel)
      return `${currentLabel.toLowerCase().replace(/\s+/g, '-')}-${Math.random().toString(36).substring(2, 7)}`;
    return `custom-date-input-${Math.random().toString(36).substring(2, 9)}`;
  });

  _startDate = signal<Date | null>(null);
  _endDate = signal<Date | null>(null);
  _formDisabled = signal(false);
  isDisabled = computed(() => this._formDisabled() || this.disabled());
  ngControl: NgControl | null = null;
  isCalendarOpen = signal(false);
  private _overlayRef: OverlayRef | null = null;
  @ViewChild('calendarTemplate') calendarTemplate!: TemplateRef<any>;
  calendarViewDate = signal(new Date());

  daysOfWeek: string[] = [];

  hoveredDate = signal<Date | null>(null);

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};
  isFocused = signal(false);

  hasValue = computed(() => {
    if (this.selectionMode() === 'range') {
      return !!(this._startDate() && this._endDate());
    }
    return !!this._startDate();
  });

  isInvalid = computed(() => {
    return this.ngControl?.invalid && (this.ngControl?.touched || this.ngControl?.dirty);
  });

  constructor(
    private _injector: Injector,
    private _datePipe: DatePipe,
    private _translate: TranslateService,
    private _elementRef: ElementRef,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef
  ) {
    this._setDaysOfWeek();
    this._translate.onLangChange.subscribe(() => {
      this._setDaysOfWeek();
    });
  }

  private _setDaysOfWeek(): void {
    this.daysOfWeek = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'];
  }

  onFocus(): void {
    this.isFocused.set(true);
  }

  onBlur(): void {
    this.isFocused.set(false);
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this._overlayRef &&
      this.isCalendarOpen() &&
      !this._elementRef.nativeElement.contains(event.target) &&
      !this._overlayRef.overlayElement.contains(event.target as Node)
    ) {
      this._closeCalendar();
    }
  }

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, null);
    if (this.ngControl) this.ngControl.valueAccessor = this;
    this.calendarViewDate.set(this._startDate() || new Date());
  }

  onDayHover(day: CalendarDay) {
    if (this.selectionMode() === 'range' && this._startDate() && !this._endDate()) {
      if (day.isCurrentMonth) {
        this.hoveredDate.set(day.date);
      }
    }
  }

  onGridMouseLeave() {
    this.hoveredDate.set(null);
  }

  get formattedValue(): string {
    if (this.selectionMode() === 'range') {
      const start = this._startDate();
      const end = this._endDate();
      if (start && end) {
        const formattedStart = this._datePipe.transform(start, 'yyyy-MM-dd') || '';
        const formattedEnd = this._datePipe.transform(end, 'yyyy-MM-dd') || '';
        return `${formattedStart} - ${formattedEnd}`;
      }
      return '';
    }
    const value = this._startDate();
    return value ? this._datePipe.transform(value, 'yyyy-MM-dd') || '' : '';
  }

  calendarGrid = computed(() => {
    const viewDate = this.calendarViewDate();
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const totalDays = lastDayOfMonth.getDate();
    const grid: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startTime = this._startDate()?.getTime();
    const endTime = this._endDate()?.getTime();
    const hoverTime = this.hoveredDate()?.getTime();

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek; i > 0; i--) {
      const prevDate = new Date(year, month - 1, prevMonthLastDay - i + 1);
      grid.push({
        date: prevDate,
        day: prevDate.getDate(),
        id: prevDate.getTime(),
        isCurrentMonth: false,
        isSelected: false,
        isToday: false,
        isRangeStart: false,
        isRangeEnd: false,
        isInPreviewRange: false,
      });
    }

    for (let i = 1; i <= totalDays; i++) {
      const currentDate = new Date(year, month, i);
      currentDate.setHours(0, 0, 0, 0);
      const currentTime = currentDate.getTime();

      const isToday = currentTime === today.getTime();
      let isSelected = false;
      let isRangeStart = false;
      let isRangeEnd = false;
      let isInPreviewRange = false;

      if (this.selectionMode() === 'single' && startTime) {
        isSelected = currentTime === startTime;
      } else if (this.selectionMode() === 'range') {
        if (startTime && endTime) {
          // A final, selected range
          if (currentTime >= startTime && currentTime <= endTime) {
            isSelected = true;
            isRangeStart = currentTime === startTime;
            isRangeEnd = currentTime === endTime;
          }
        } else if (startTime) {
          // A selection is in progress, only a start date is set.
          isRangeStart = currentTime === startTime;
          isSelected = isRangeStart; // Only the start date is marked as selected.

          // Handle the preview range on hover
          if (hoverTime && hoverTime !== startTime) {
            const start = Math.min(startTime, hoverTime);
            const end = Math.max(startTime, hoverTime);
            if (currentTime >= start && currentTime <= end) {
              isInPreviewRange = true;
            }
          }
        }
      }

      grid.push({
        date: currentDate,
        day: i,
        id: currentDate.getTime(),
        isCurrentMonth: true,
        isSelected,
        isToday,
        isRangeStart,
        isRangeEnd,
        isInPreviewRange,
      });
    }

    const gridEndFiller = 42 - grid.length;
    for (let i = 1; i <= gridEndFiller; i++) {
      const nextDate = new Date(year, month + 1, i);
      grid.push({
        date: nextDate,
        day: nextDate.getDate(),
        id: nextDate.getTime(),
        isCurrentMonth: false,
        isSelected: false,
        isToday: false,
        isRangeStart: false,
        isRangeEnd: false,
        isInPreviewRange: false,
      });
    }
    return grid;
  });

  toggleCalendar() {
    if (!this.isDisabled()) {
      if (this.isCalendarOpen()) {
        this._closeCalendar();
      } else {
        this._openCalendar();
      }
    }
  }

  private _openCalendar() {
    if (!this._overlayRef) {
      const config = new OverlayConfig({
        hasBackdrop: false,
        positionStrategy: this._overlay
          .position()
          .flexibleConnectedTo(this._elementRef)
          .withPositions([
            {
              originX: 'start',
              originY: 'bottom',
              overlayX: 'start',
              overlayY: 'top',
              offsetY: 4,
            },
            {
              originX: 'start',
              originY: 'top',
              overlayX: 'start',
              overlayY: 'bottom',
              offsetY: -4,
            },
            {
              originX: 'end',
              originY: 'bottom',
              overlayX: 'end',
              overlayY: 'top',
              offsetY: 4,
            },
          ]),
        scrollStrategy: this._overlay.scrollStrategies.reposition(),
        width: '264px', // Mismo ancho que el calendario original
      });
      this._overlayRef = this._overlay.create(config);
    }

    const portal = new TemplatePortal(this.calendarTemplate, this._viewContainerRef);
    this._overlayRef!.attach(portal);
    this.isCalendarOpen.set(true);
  }

  private _closeCalendar() {
    if (this._overlayRef) {
      this._overlayRef.detach();
      this.isCalendarOpen.set(false);
    }
  }

  selectDate(day: CalendarDay) {
    try {
      if (!day.isCurrentMonth) {
        this.calendarViewDate.set(day.date);
        return;
      }

      if (this.selectionMode() === 'single') {
        this._startDate.set(day.date);
        this._endDate.set(null);
        this.onChange(this._datePipe.transform(day.date, 'yyyy-MM-dd'));
        this._closeCalendar();
        return;
      }

      // Range Mode Logic
      const startDate = this._startDate();
      const endDate = this._endDate();

      if (!startDate || (startDate && endDate)) {
        // No start date OR full range selected -> start a new selection
        this._startDate.set(day.date);
        this._endDate.set(null);
        this.hoveredDate.set(null);
        return;
      } else {
        // Start date is set, so this MUST be the end date
        this._endDate.set(day.date);
        this.hoveredDate.set(null);

        // Ensure start is before end
        if (this._startDate()!.getTime() > this._endDate()!.getTime()) {
          // Swap them
          const temp = this._startDate();
          this._startDate.set(this._endDate());
          this._endDate.set(temp);
        }

        this.onChange({
          start: this._datePipe.transform(this._startDate(), 'yyyy-MM-dd'),
          end: this._datePipe.transform(this._endDate(), 'yyyy-MM-dd'),
        });
        this._closeCalendar();
        return;
      }
    } catch (error: any) {
      console.error(error);
    }
  }

  previousMonth() {
    this.calendarViewDate.update((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }

  nextMonth() {
    this.calendarViewDate.update((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }

  get monthName() {
    return this.calendarViewDate().toLocaleString('es', { month: 'long' });
  }

  get year() {
    return this.calendarViewDate().getFullYear();
  }

  writeValue(value: any): void {
    const parseLocal = (val: any) => {
      if (!val) return new Date(NaN);
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const [year, month, day] = val.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(val);
    };

    if (this.selectionMode() === 'range') {
      if (value && value.start && value.end) {
        const startDate = parseLocal(value.start);
        const endDate = parseLocal(value.end);
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          this._startDate.set(startDate);
          this._endDate.set(endDate);
          this.calendarViewDate.set(startDate);
          return;
        }
      }
      this._startDate.set(null);
      this._endDate.set(null);
    } else {
      if (value) {
        const date = parseLocal(value);
        if (!isNaN(date.getTime())) {
          this._startDate.set(date);
          this._endDate.set(null); // Ensure end date is cleared in single mode
          this.calendarViewDate.set(date);
          return;
        }
      }
      this._startDate.set(null);
      this._endDate.set(null);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this._formDisabled.set(isDisabled);
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
  }
}
